/**
 * Mixpanel identity lifecycle: the super properties every event carries, and
 * the teardown that runs on sign-out.
 *
 * Both live here rather than inline in `main.tsx` because they are two halves
 * of one contract — `mixpanel.reset()` clears super properties, so whatever
 * registers them must also be callable after a reset.
 */

import { clearAcquisitionSource } from "./acquisition";
import { clearAppOpenHistory } from "./appOpenHistory";
import mixpanel from "./mixpanel";

/**
 * Properties attached to every event from this device. Evaluated at call time
 * (not module load) so a post-reset re-registration reads current values.
 */
function superProperties(): Record<string, unknown> {
  return {
    platform: import.meta.env.VITE_PLATFORM,
    env: import.meta.env.VITE_ENV,
    isLandingPage:
      import.meta.env.VITE_PLATFORM === "web" &&
      !window.location.hostname.includes("app."),
  };
}

export function registerSuperProperties(): void {
  mixpanel.register_once(superProperties());
}

/**
 * Sever the link between this device and the user who just signed out.
 *
 * Without `mixpanel.reset()`, two people signing in and out on one device
 * share a `distinct_id`, so their events merge into a single Mixpanel profile
 * — the same class of bug that `clearCachedPushToken` exists to prevent.
 *
 * `reset()` also clears super properties (see its docstring: "Clears super
 * properties and generates a new random distinct_id"), so they are
 * re-registered immediately. Skipping that would silently drop `platform` /
 * `env` from every event for the rest of the page's life.
 *
 * The two `localStorage` clears cover analytics state that is device-scoped in
 * storage but user-scoped in meaning. Session timestamps
 * (`mash:last_session_at` / `mash:last_active_at`) are deliberately left
 * alone: they describe this browser's visit, not the user, and clearing
 * `last_active_at` would make the next foreground look like a cold boot and
 * emit a spurious `session_started`.
 */
export function resetAnalyticsIdentity(): void {
  mixpanel.reset();
  registerSuperProperties();
  clearAcquisitionSource();
  clearAppOpenHistory();
}
